import { APP_DEFAULT_LOCALE } from '@/constants/i18n.constants'
import { env } from '@/env.mjs'
import {
  AUTHOR_PAGE_PATH,
  CATEGORY_PAGE_PATH,
  PAGE_URL_PATH,
  ROOT_APPS_PATH,
  ROOT_BLOG_PAGE_PATH,
  ROOT_CASE_STUDIES_PATH,
  ROOT_PAGE_PATH,
  SEARCH_BLOG_PAGE_PATH,
} from '@/lib/constants'
import { getOriginUrl } from '@/lib/general-helpers'
import type { AppLocale } from '@/types/general'

export function getLocalePrefix(locale?: AppLocale) {
  if (!locale || locale === APP_DEFAULT_LOCALE) {
    return ''
  }

  return `/${locale}`
}

function buildLocalePrefixedPath(pathname: string, locale?: AppLocale) {
  const normalizedPath = pathname.startsWith('/') ? pathname : `/${pathname}`
  return `${getLocalePrefix(locale)}${normalizedPath}`
}

export const getFullUrl = (relativeLink: string | null | undefined) => {
  if (relativeLink == null) {
    return undefined
  }

  if (relativeLink === '/') {
    return env.APP_PUBLIC_URL
  }

  if (relativeLink[0] === '/') {
    return `${env.APP_PUBLIC_URL}${relativeLink}`
  }

  return `${env.APP_PUBLIC_URL}/${relativeLink}`
}

export const getBlogLink = (locale?: AppLocale) => {
  return buildLocalePrefixedPath(ROOT_BLOG_PAGE_PATH, locale)
}

export const getBlogPageLink = (page?: string | number | null, locale?: AppLocale) => {
  if (page == null) {
    return ''
  }
  return buildLocalePrefixedPath(`${ROOT_BLOG_PAGE_PATH}${PAGE_URL_PATH}/${page}`, locale)
}

export const getBlogSearchLink = (locale?: AppLocale) => {
  return buildLocalePrefixedPath(SEARCH_BLOG_PAGE_PATH, locale)
}

export const getArticleLink = (slug?: string | null, locale?: AppLocale) => {
  if (slug == null) {
    return ''
  }
  return buildLocalePrefixedPath(`${ROOT_BLOG_PAGE_PATH}/${slug}`, locale)
}

export const getArticleUrl = (slug?: string | null, locale?: AppLocale) => {
  if (slug == null) {
    return ''
  }

  return getFullUrl(getArticleLink(slug, locale))
}

export const getBlogCategoryLink = (slug?: string | null, locale?: AppLocale) => {
  if (slug == null) {
    return ''
  }
  return buildLocalePrefixedPath(`${CATEGORY_PAGE_PATH}/${slug}`, locale)
}

export const getBlogCategoryUrl = (slug?: string | null, locale?: AppLocale) => {
  if (slug == null) {
    return ''
  }

  return getFullUrl(getBlogCategoryLink(slug, locale))
}

export const getBlogCategoryPageLink = (
  slug?: string | null,
  page?: string | number | null,
  locale?: AppLocale,
) => {
  if (slug == null || page == null) {
    return ''
  }
  return buildLocalePrefixedPath(`${CATEGORY_PAGE_PATH}/${slug}${PAGE_URL_PATH}/${page}`, locale)
}

export const getAuthorLink = (slug?: string | null, locale?: AppLocale) => {
  if (slug == null) {
    return ''
  }
  return buildLocalePrefixedPath(`${AUTHOR_PAGE_PATH}/${slug}`, locale)
}

export const getAuthorUrl = (slug?: string | null, locale?: AppLocale) => {
  if (slug == null) {
    return ''
  }
  return getFullUrl(getAuthorLink(slug, locale))
}

export const getAuthorPageLink = (
  slug?: string | null,
  page?: string | number | null,
  locale?: AppLocale,
) => {
  if (slug == null || page == null) {
    return ''
  }
  return buildLocalePrefixedPath(`${AUTHOR_PAGE_PATH}/${slug}${PAGE_URL_PATH}/${page}`, locale)
}

export const getPageLink = (fullPath?: string | null, locale?: AppLocale) => {
  if (fullPath == null || fullPath === '/' || fullPath === ROOT_PAGE_PATH) {
    return ''
  }

  const path = fullPath.replace('index/', '')
  return buildLocalePrefixedPath(path, locale)
}

export const getPageUrl = (fullPath?: string | null, locale?: AppLocale) => {
  if (fullPath == null) {
    return ''
  }

  return getFullUrl(getPageLink(fullPath, locale))
}

export const getCaseStudyLink = (slug?: string | null, locale?: AppLocale) => {
  if (slug == null) {
    return ''
  }
  return buildLocalePrefixedPath(`${ROOT_CASE_STUDIES_PATH}/${slug}`, locale)
}

export const getCaseStudiesRootPageLink = (locale?: AppLocale) => {
  return buildLocalePrefixedPath(ROOT_CASE_STUDIES_PATH, locale)
}

export const getAppRootLink = (locale?: AppLocale) => {
  return buildLocalePrefixedPath(ROOT_APPS_PATH, locale)
}

export const getAppLink = (slug?: string | null, category?: string | null, locale?: AppLocale) => {
  if (slug == null || category == null) {
    return ''
  }
  return buildLocalePrefixedPath(`${ROOT_APPS_PATH}/${category}/${slug}`, locale)
}

export const getAppCategoryLink = (category?: string | null, locale?: AppLocale) => {
  if (category == null) {
    return ''
  }
  return buildLocalePrefixedPath(`${ROOT_APPS_PATH}/${category}`, locale)
}

/**
 * Checks if a link is an internal app link (same hostname) or external
 */
export function isAppLink(link: string): boolean {
  try {
    const baseUrl = getOriginUrl()
    const url = new URL(link, baseUrl)
    return url.hostname === new URL(baseUrl).hostname
  } catch (_e) {
    return false
  }
}

/**
 * Checks if a value matches the pathname, accounting for trailing slashes
 */
export const isValueEqualToPathname = (value: string, pathname: string) =>
  value === pathname || `${value}/` === pathname
